export type SearchIntent = 'informational' | 'commercial' | 'transactional' | 'navigational';

export interface IntentAnalysis {
  intent: SearchIntent;
  confidence: number;
  indicators: string[];
}

export interface IntentDistribution {
  informational: number;
  commercial: number;
  transactional: number;
  navigational: number;
  total: number;
}

// Intent classification patterns
const INTENT_PATTERNS = {
  informational: {
    patterns: [
      // Question patterns (high priority)
      /^(how|what|why|when|where|who|which|can|should|will|would|could|is|are|does|do|did)\b/i,
      // Information seeking
      /\b(guide|tutorial|learn|explain|meaning|definition|tips|advice|help|support)\b/i,
      /\b(examples|sample|template|beginner|steps|process|method|way)\b/i,
      // Content patterns
      /\b(article|blog|news|information|facts|data|research|study)\b/i,
    ],
    weight: 1.2  // Higher weight for question-based queries
  },
  
  commercial: {
    patterns: [
      // Comparison patterns
      /\b(best|top|better|compare|comparison|vs|versus|alternative)\b/i,
      /\b(review|reviews|rating|ratings|pros|cons|opinion)\b/i,
      // Research patterns (removed overlapping price terms)
      /\b(affordable|expensive|budget|pricing)\b/i,
      /\b(quality|premium|luxury|professional|enterprise|business)\b/i,
      // Decision making
      /\b(choose|pick|select|decide|recommend|suggestion|option)\b/i,
    ],
    weight: 1.0
  },
  
  transactional: {
    patterns: [
      // Purchase patterns
      /\b(buy|purchase|order|shop|shopping|store|sale|deal)\b/i,
      /\b(price|cost|cheap|discount|coupon|offer|free|shipping)\b/i,
      // Specific action patterns (removed generic 'get')
      /\b(download|hire|book|reserve|subscribe|signup|register)\b/i,
      // Location patterns (fixed 'me' to be more specific)
      /\b(online|delivery|local|near\s+me|location|address)\b/i,
      // Commercial terms
      /\b(supplier|vendor|service)\b/i,
    ],
    weight: 1.0
  },
  
  navigational: {
    patterns: [
      // Strong navigational signals - login and access
      /\b(login|log\s+in|sign\s+in|signin|sign\s+up|signup|account|dashboard)\b/i,
      // Brand + navigation combinations
      /\b(official\s+website|main\s+website|company\s+website|homepage|official\s+site)\b/i,
      // Direct URL/domain patterns
      /^[a-z\s]+\.(com|org|net|co\.uk|edu|gov)$/i,
      /\b(www\.|https?:\/\/)\b/i,
      // Navigation to specific pages
      /\b(contact\s+us|about\s+us|careers|support\s+page|help\s+center)\b/i,
      // Brand name + basic navigation (when brand is clearly identified)
      /\b(home|contact|about|support)\s+(page|section)?\b/i,
    ],
    weight: 1.0  // Reduced weight to prevent over-classification
  }
};

/**
 * Classify a search query by intent using pattern matching
 */
export function classifySearchIntent(query: string): IntentAnalysis {
  if (!query || typeof query !== 'string') {
    return {
      intent: 'informational',
      confidence: 0.3,
      indicators: []
    };
  }

  const scores = {
    informational: 0,
    commercial: 0,
    transactional: 0,
    navigational: 0
  };
  
  const indicators: Record<SearchIntent, string[]> = {
    informational: [],
    commercial: [],
    transactional: [],
    navigational: []
  };

  // Score each intent type
  Object.entries(INTENT_PATTERNS).forEach(([intent, config]) => {
    config.patterns.forEach(pattern => {
      const matches = query.match(pattern);
      if (matches) {
        scores[intent as SearchIntent] += config.weight;
        // Store the matched indicator (first capture group or full match)
        const indicator = matches[1] || matches[0];
        if (indicator && typeof indicator === 'string') {
          indicators[intent as SearchIntent].push(indicator.toLowerCase());
        }
      }
    });
  });

  // Determine primary intent with proper tie-breaking
  const maxScore = Math.max(...Object.values(scores));
  let primaryIntent: SearchIntent = 'informational'; // default
  
  if (maxScore > 0) {
    // Priority order for tie-breaking: informational > commercial > navigational > transactional
    const intentPriority: SearchIntent[] = ['informational', 'commercial', 'navigational', 'transactional'];
    
    // Find the highest-priority intent with the maximum score
    for (const intent of intentPriority) {
      if (scores[intent] === maxScore) {
        primaryIntent = intent;
        break;
      }
    }
  }

  // Calculate confidence based on score difference
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
  const confidence = totalScore > 0 ? scores[primaryIntent] / totalScore : 0.3;

  return {
    intent: primaryIntent,
    confidence: Math.min(confidence, 1.0),
    indicators: indicators[primaryIntent]
  };
}

/**
 * Get intent distribution for a collection of queries
 */
export function getIntentDistribution(queries: Array<{ query: string }>): IntentDistribution {
  const distribution = {
    informational: 0,
    commercial: 0,
    transactional: 0,
    navigational: 0,
    total: queries.length
  };

  queries.forEach(({ query }) => {
    const analysis = classifySearchIntent(query);
    distribution[analysis.intent]++;
  });

  return distribution;
}

/**
 * Get intent icon for display
 */
export function getIntentIcon(intent: SearchIntent): string {
  const icons = {
    informational: '🔍',
    commercial: '💰', 
    transactional: '🛒',
    navigational: '🧭'
  };
  return icons[intent];
}

/**
 * Get intent color for styling
 */
export function getIntentColor(intent: SearchIntent): string {
  const colors = {
    informational: 'blue',
    commercial: 'yellow',
    transactional: 'green',
    navigational: 'purple'
  };
  return colors[intent];
}

/**
 * Get strategy recommendations based on intent
 */
export function getIntentStrategy(intent: SearchIntent): {
  title: string;
  description: string;
  actions: string[];
} {
  const strategies = {
    informational: {
      title: 'Informational Content Strategy',
      description: 'Focus on educational content that answers questions and provides value.',
      actions: [
        'Create comprehensive guides and tutorials',
        'Optimize for featured snippets',
        'Build topical authority with in-depth content',
        'Use clear headings and structured data'
      ]
    },
    commercial: {
      title: 'Commercial Research Strategy', 
      description: 'Help users evaluate options and make informed decisions.',
      actions: [
        'Create comparison pages and reviews',
        'Highlight unique selling propositions',
        'Include social proof and testimonials',
        'Optimize for "best of" and comparison queries'
      ]
    },
    transactional: {
      title: 'Transactional Conversion Strategy',
      description: 'Optimize for immediate action and conversions.',
      actions: [
        'Streamline the purchase funnel',
        'Include clear calls-to-action',
        'Optimize product/service pages',
        'Focus on local SEO if applicable'
      ]
    },
    navigational: {
      title: 'Navigational Brand Strategy',
      description: 'Ensure brand visibility and easy site navigation.',
      actions: [
        'Secure top rankings for brand terms',
        'Optimize homepage and key landing pages',
        'Improve site search and navigation',
        'Monitor competitor brand bidding'
      ]
    }
  };
  
  return strategies[intent];
}